import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Stack,
  Popover,
  TextField,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpenseForm from './ExpenseForm';

const Expenses = ({
  user,
  expenses,
  isLoadingExpenses,
  expensesError,
  setExpensesError,
  fetchExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  channels = [],
  selectedMonth,
  setSelectedMonth,
  monthOptions = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (user) {
      fetchExpenses();
    }
  }, [user, fetchExpenses]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    const formatAsLocale = (date) => {
      if (!date || Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    };

    if (typeof timestamp.toDate === 'function') {
      return formatAsLocale(timestamp.toDate());
    }

    if (typeof timestamp === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
        const [year, month, day] = timestamp.split('-').map(Number);
        return formatAsLocale(new Date(year, month - 1, day));
      }
      if (timestamp.includes('T')) {
        return formatAsLocale(new Date(timestamp));
      }
    }

    return formatAsLocale(new Date(timestamp));
  };

  const handleOpenForm = (event, expense = null) => {
    setAnchorEl(event.currentTarget);
    setEditingExpense(expense);
  };

  const handleCloseForm = () => {
    setAnchorEl(null);
    setEditingExpense(null);
  };

  const handleSubmitExpense = async (formData, expenseId) => {
    setIsSubmitting(true);
    setExpensesError('');
    try {
      const success = expenseId
        ? await updateExpense(expenseId, formData)
        : await createExpense(formData);
      if (success) {
        handleCloseForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (event, expenseId) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeletingExpenseId(expenseId);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteAnchorEl(null);
    setDeletingExpenseId(null);
    setIsDeleting(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!deletingExpenseId) return;
    if (isDeleting) return;
    setIsDeleting(true);

    const success = await deleteExpense(deletingExpenseId);
    if (success) {
      handleDeleteConfirmClose();
    } else {
      setIsDeleting(false);
    }
  };

  const selectedLabel = monthOptions.find(o => o.value === selectedMonth)?.label || '';

  if (isLoadingExpenses) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Month Selector and Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Expenses ({expenses.length})
        </Typography>
        {monthOptions.length > 0 && (
          <TextField
            select
            size="small"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 180 }}
            aria-label="Select month"
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={(e) => handleOpenForm(e)}
          sx={{ textTransform: 'none' }}
        >
          Add Expense
        </Button>
      </Box>

      {expensesError && (
        <Alert severity="error" sx={{ mb: 3 }} aria-live="polite" aria-atomic="true">
          {expensesError}
        </Alert>
      )}

      {expenses.length === 0 ? (
        <Alert severity="info">
          {selectedMonth && selectedMonth !== 'all'
            ? `No expenses for ${selectedLabel}.`
            : 'No expenses recorded yet. Click "Add Expense" to get started.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="Expenses">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {!isMobile && <TableCell>Type</TableCell>}
                {!isMobile && <TableCell>Notes</TableCell>}
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => {
                const isAutoCommission = expense.autoGenerated === true;
                const commissionLabel = expense.commissionPercentage != null
                  ? `Auto — commission ${expense.commissionPercentage}%`
                  : 'Auto — commission';
                const commissionTooltip = `Managed by the source sale. Edit the sale to change it.${
                  expense.sourceSalesTransactionId ? ` Sale ID: ${expense.sourceSalesTransactionId}` : ''
                }`;
                return (
                  <TableRow key={expense.id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                    <TableCell>{formatDate(expense.expenseDate)}</TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ textTransform: 'capitalize' }}>{expense.expenseType}</Box>
                          {isAutoCommission && (
                            <Chip
                              label={commissionLabel}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                    )}
                    {!isMobile && <TableCell>{expense.expenseNotes || '-'}</TableCell>}
                    <TableCell align="right">${parseFloat(expense.expenseAmount).toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={isAutoCommission ? commissionTooltip : 'Edit'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenForm(e, expense)}
                              aria-label="Edit"
                              disabled={isAutoCommission}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isAutoCommission ? commissionTooltip : 'Delete'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleDeleteClick(e, expense.id)}
                              aria-label="Delete"
                              disabled={isAutoCommission}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ backgroundColor: 'grey.100', borderTop: '2px solid #ddd' }}>
                <TableCell colSpan={isMobile ? 1 : 3} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  Total Expenses:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.dark' }}>
                  ${expenses.reduce((sum, exp) => sum + parseFloat(exp.expenseAmount || 0), 0).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      {/* Expense Form Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseForm}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <ExpenseForm
          expense={editingExpense}
          onSubmit={handleSubmitExpense}
          onCancel={handleCloseForm}
          isSubmitting={isSubmitting}
          channels={channels}
        />
      </Popover>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteConfirmClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deleting an expense is irreversible. The expense and all associated data will be deleted. Are you sure?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={handleDeleteConfirmClose} variant="outlined">
              Go Back
            </Button>
            <Button
              onClick={handleDeleteConfirmed}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes, Delete'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default Expenses;
